<template>
  <v-app>
    <!-- Navigation Drawer (desktop) -->
    <v-navigation-drawer
      v-if="isAuthenticated"
      v-model="drawer"
      :permanent="!mobile"
      :temporary="mobile"
      width="240"
      color="primary-darken-1"
      dark
    >
      <div class="d-flex align-center pa-4">
        <v-icon size="28" color="white" class="mr-2">mdi-grass</v-icon>
        <span class="text-h6 text-white font-weight-bold">MowApp</span>
      </div>

      <v-divider class="border-opacity-25" />

      <div v-if="isOnTrial" class="px-3 pt-3">
        <v-card color="info" rounded="lg" class="pa-3 text-white" elevation="4">
          <p class="text-caption text-uppercase font-weight-bold mb-1">Growth Trial</p>
          <p class="text-body-2 mb-1 font-weight-bold">
            <template v-if="trialDaysLeft > 0">{{ trialDaysLeft }} day{{ trialDaysLeft === 1 ? '' : 's' }} left</template>
            <template v-else>Upgrade Requested</template>
          </p>
          <p class="text-caption mb-0">Lock in with sales.</p>
        </v-card>
      </div>

      <div v-else-if="isFreePlan" class="px-3 pt-3">
        <v-card color="secondary" rounded="lg" class="pa-3 text-white" elevation="4">
          <p class="text-caption text-uppercase font-weight-bold mb-1">Free Plan</p>
          <p class="text-body-2 mb-3">Upgrade to Growth for unlimited customers, team management, and premium tools.</p>
          <v-btn
            block
            color="white"
            variant="flat"
            size="small"
            @click="goToPricingUpgrade"
          >
            Upgrade to Growth
          </v-btn>
        </v-card>
      </div>

      <v-list nav density="comfortable" base-color="white">
        <v-list-item
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          :prepend-icon="item.icon"
          :title="item.title"
          rounded="lg"
          class="mx-2 my-1"
          color="white"
        />
      </v-list>
    </v-navigation-drawer>

    <!-- App Bar -->
    <v-app-bar v-if="isAuthenticated" flat color="surface" border="b">
      <v-app-bar-nav-icon v-if="mobile" @click="drawer = !drawer" />
      <v-app-bar-title class="text-body-1 font-weight-medium">
        {{ businessName }}
      </v-app-bar-title>

      <template #append>
        <v-btn
          v-if="isFreePlan"
          color="secondary"
          class="mr-2"
          prepend-icon="mdi-rocket-launch-outline"
          @click="goToPricingUpgrade"
        >
          Upgrade
        </v-btn>
        <v-btn icon size="small" class="mr-1">
          <v-icon>mdi-bell-outline</v-icon>
        </v-btn>
        <v-menu>
          <template #activator="{ props }">
            <v-btn icon size="small" v-bind="props">
              <v-avatar color="primary" size="32">
                <span class="text-caption text-white">{{ userInitials }}</span>
              </v-avatar>
            </v-btn>
          </template>
          <v-list density="compact" width="200">
            <v-list-item prepend-icon="mdi-account" :title="fullName" :subtitle="auth.user?.email" />
            <v-divider />
            <v-list-item v-if="auth.user?.role === 'owner'" prepend-icon="mdi-account-multiple" title="Team" to="/team" />
            <v-list-item prepend-icon="mdi-lifebuoy" title="Support" to="/support" />
            <v-list-item prepend-icon="mdi-cog" title="Settings" to="/settings" />
            <v-list-item prepend-icon="mdi-logout" title="Logout" @click="handleLogout" />
          </v-list>
        </v-menu>
      </template>
    </v-app-bar>

    <!-- Main Content -->
    <v-main>
      <router-view />
    </v-main>

    <v-footer app height="40" class="px-4 d-flex align-center justify-center bg-grey-lighten-5">
      <span class="text-caption text-medium-emphasis">
        MowApp is provided by
        <a
          href="https://danstechservices.us/"
          target="_blank"
          rel="noopener noreferrer"
          class="provider-link"
        >
          Dan's Tech Services
        </a>
      </span>
    </v-footer>

    <!-- Bottom Navigation (mobile) -->
    <v-bottom-navigation v-if="isAuthenticated && mobile" grow color="primary">
      <v-btn v-for="item in navItems.slice(0, 4)" :key="item.to" :to="item.to">
        <v-icon>{{ item.icon }}</v-icon>
        <span class="text-caption">{{ item.title }}</span>
      </v-btn>
    </v-bottom-navigation>

    <!-- Onboarding Checklist (owners only, until complete or dismissed) -->

    <!-- Global Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      location="bottom right"
    >
      {{ snackbar.message }}
      <template #actions>
        <v-btn variant="text" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useAuthStore } from '@/stores/auth'
import { useSnackbarStore } from '@/stores/snackbar'
import { useOnboardingStore } from '@/stores/onboarding'
import { useRouter, useRoute } from 'vue-router'

export default defineComponent({
  name: 'App',
  setup() {
    const { mobile } = useDisplay()
    const auth = useAuthStore()
    const snackbar = useSnackbarStore()
    const onboarding = useOnboardingStore()
    const router = useRouter()
    const route = useRoute()
    const drawer = ref(true)

    // Load + check onboarding whenever user is authenticated
    watch(
      () => auth.user,
      (user) => {
        if (user && user.role === 'owner') {
          onboarding.load(user.id)
          onboarding.checkProgress(user.id)
        } else {
          onboarding.reset()
        }
      },
      { immediate: true },
    )

    // Re-check when navigating between views (picks up newly created items)
    watch(
      () => route.path,
      () => {
        if (auth.user?.role === 'owner' && onboarding.visible) {
          onboarding.checkProgress(auth.user.id)
        }
      },
    )

    const navItems = computed(() => {
      const ownerItems = [
        ...(onboarding.visible
          ? [{ title: 'Get Started', icon: 'mdi-flag-checkered', to: '/get-started' }]
          : []),
        { title: 'Dashboard', icon: 'mdi-view-dashboard', to: '/dashboard' },
        { title: 'Finances', icon: 'mdi-cash-multiple', to: '/finances' },
        { title: 'Customers', icon: 'mdi-account-group', to: '/customers' },
        { title: 'Routes', icon: 'mdi-map-marker-path', to: '/routes' },
        { title: 'Invoices', icon: 'mdi-file-document-outline', to: '/invoices' },
        { title: 'Team', icon: 'mdi-account-multiple', to: '/team' },
        { title: 'Support', icon: 'mdi-lifebuoy', to: '/support' },
        { title: 'Settings', icon: 'mdi-cog', to: '/settings' },
      ]

      const employeeItems = [
        { title: 'Routes', icon: 'mdi-map-marker-path', to: '/routes' },
        { title: 'Support', icon: 'mdi-lifebuoy', to: '/support' },
        { title: 'Settings', icon: 'mdi-cog', to: '/settings' },
      ]

      const items = auth.user?.role === 'owner' ? ownerItems : employeeItems

      if (auth.user?.isSuperAdmin) {
        items.push({ title: 'Admin', icon: 'mdi-security', to: '/admin' })
      }

      return items
    })

    const isAuthenticated = computed(() => auth.isAuthenticated)
    const hasUpgradeRequest = computed(() => !!auth.business?.needs_sales_followup)
    const isOnTrial = computed(() => !!auth.business?.growth_trial_ends_at || hasUpgradeRequest.value)
    const isFreePlan = computed(() => auth.user?.planTier === 'free' && !isOnTrial.value)
    const trialDaysLeft = computed(() => {
      const trialEnd = auth.business?.growth_trial_ends_at
      if (!trialEnd) return 0
      return Math.max(0, Math.ceil((new Date(trialEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    })
    const fullName = computed(() => auth.fullName)
    const businessName = computed(() => auth.businessName)
    const userInitials = computed(() => {
      if (!auth.user) return ''
      return `${auth.user.firstName?.[0] || ''}${auth.user.lastName?.[0] || ''}`.toUpperCase()
    })

    async function handleLogout() {
      await auth.logout()
      router.push('/login')
    }

    function goToPricingUpgrade() {
      router.push({ name: 'pricing', query: { intent: 'upgrade' } })
    }

    return {
      drawer, mobile, auth, snackbar, onboarding, navItems,
      isAuthenticated, isFreePlan, isOnTrial, trialDaysLeft,
      fullName, businessName, userInitials,
      handleLogout, goToPricingUpgrade,
    }
  },
})
</script>

<style scoped>
.provider-link {
  color: #1f6f4a;
  text-decoration: none;
  font-weight: 600;
}

.provider-link:hover {
  text-decoration: underline;
}
</style>
